import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  HttpLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from 'apollo-utilities';
import React from 'react';

import { LOOM_URI, LOOM_WS_URI } from 'constants/routes';
import { LOOM_VERSION } from 'constants/versions';
import * as logger from 'utilities/loggerx';
import * as sentry from 'utilities/sentry';

import { typePolicies } from './typePolicies';

// Operations added to this list will never be batched by Apollo Client.
const UNBATCHED_OPERATIONS = ['getWorkspaceBillingDetails'];

let client = null;
let clientInsights = null;
let cache = null;
let link = null;

/**
 * Requests a graphql mutation with in built error logger and handler for Error/GenericError
 * @param {gql} mutation The mutation gql object, response must be aliased as result w/ __typename
 * @param {Object}  variables The variables to pass to a mutation
 * @param {function} onError error handler for botched mutation
 * @returns {result} The result of the mutation
 */
export const requestGraphQLMutationWithErrorHandler = async (
  mutation,
  variables,
  onError = () => {}
) => {
  const gqlClient = getGraphQLClient();

  try {
    const { data } = await gqlClient.mutate({
      mutation,
      variables,
    });

    const resultType = data?.result?.__typename;

    if (['Error', 'GenericError'].includes(resultType)) {
      throw new Error(data?.result?.message);
    }

    return data;
  } catch (err) {
    const operationName = mutation.definitions.reduce((str, op) => {
      return `${str} ${op?.name?.value}`;
    }, '');

    logger.error(
      err,
      `Error while processing graphql mutation ${operationName}`,
      {
        ...variables,
      }
    );

    onError(err);
  }
};

export const GraphQLProvider = ({ children }) => (
  <ApolloProvider client={getGraphQLClient()}>{children}</ApolloProvider>
);

const initiateGraphQLClient = () => {
  cache = new InMemoryCache({
    typePolicies,
  });

  // try batched link for now
  const batchHttpLink = new BatchHttpLink({
    uri: `${LOOM_URI}/graphql`,
    credentials: 'include',
  });

  const unbatchedHttpLink = new HttpLink({
    uri: `${LOOM_URI}/graphql`,
    credentials: 'include',
  });

  // By default, we batch operations where possible, but for any operations that we've explicitly
  // excluded from batching, we split them into their own request here.
  const httpLink = split(
    ({ operationName }) => {
      return UNBATCHED_OPERATIONS.includes(operationName);
    },
    unbatchedHttpLink,
    batchHttpLink
  );

  // Create a WebSocket link:
  const wsLink = new WebSocketLink({
    uri: `${LOOM_WS_URI}/graphql`,
    credentials: 'include',
    options: {
      reconnect: true,
    },
  });

  // using the ability to split links, you can send data to each link
  // depending on what kind of operation is being sent
  // https://www.apollographql.com/docs/link/composition.html#directional
  const partialLink = split(
    // split based on operation type
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query);

      return kind === 'OperationDefinition' && operation === 'subscription';
    },
    wsLink,
    httpLink
  );

  link = ApolloLink.from([
    onError(({ response, graphQLErrors, networkError, operation }) => {
      if (graphQLErrors) {
        response.errors.map(err =>
          sentry.logException(err, 'Error while executing graphql operation', {
            operation,
            graphQLErrors,
          })
        );
      }

      if (networkError) {
        sentry.logException(
          networkError,
          'Network Error while executing graphql operation',
          {
            operation,
            networkError,
          }
        );
      }
    }),
    partialLink,
  ]);

  client = new ApolloClient({
    link,
    fetchOptions: { credentials: 'include' },
    cache,
    name: 'web',
    version: LOOM_VERSION,
  });
};

export const getGraphQLClient = () => {
  if (!client || !cache) {
    try {
      initiateGraphQLClient();
    } catch (err) {
      console.error(err);
    }
  }

  return client;
};

export const getGraphQLClientInsights = () => {
  if (!clientInsights) {
    try {
      clientInsights = new ApolloClient({
        link: new HttpLink({
          uri: `${LOOM_URI}/insights-api/graphql`,
          credentials: 'include',
        }),
        cache: new InMemoryCache(), // unused
        name: 'web-insights',
        version: LOOM_VERSION,
        defaultOptions: {
          query: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'ignore',
          },
          // introspectionfragmentmatcher
          // workaround to avoid an error/bug with apollo
          // https://useloom.slack.com/archives/C79LNU2UF/p1598455412086700
          mutate: {
            fetchPolicy: 'no-cache',
            errorPolicy: 'ignore',
          },
        },
      });
    } catch (err) {
      console.error(err);
    }
  }

  return clientInsights;
};

export function mockClient(mockClient) {
  client = mockClient;
  clientInsights = mockClient;
  cache = new InMemoryCache();
}
